import { types, Instance, getRoot, getSnapshot } from "mobx-state-tree";
import { observable } from "mobx";
import isEqual from "lodash/isEqual";
import type { RootInstance } from "./main";
import type { IModModel } from "./ModsStore";

const MAX_HISTORY_SIZE = 50;
const DEBOUNCE_MS = 300;

type ModSnapshot = ReturnType<typeof getSnapshot<IModModel[]>>;
type EditorSnapshot = {
  modsSnapshot: ModSnapshot;
  jsonFile: unknown;
  jsonFileName?: string | null;
};

const cloneJsonSnapshot = <T>(snapshot: T): T => {
  if (snapshot == null) {
    return snapshot;
  }

  return JSON.parse(JSON.stringify(snapshot)) as T;
};

const cloneSnapshot = (snapshot: EditorSnapshot): EditorSnapshot => {
  return {
    modsSnapshot: JSON.parse(JSON.stringify(snapshot.modsSnapshot)) as ModSnapshot,
    jsonFile: cloneJsonSnapshot(snapshot.jsonFile),
    jsonFileName: snapshot.jsonFileName ?? null,
  };
};

const snapshotsEqual = (a: EditorSnapshot, b: EditorSnapshot): boolean => {
  return (
    isEqual(a.modsSnapshot, b.modsSnapshot) &&
    isEqual(a.jsonFile, b.jsonFile) &&
    (a.jsonFileName ?? null) === (b.jsonFileName ?? null)
  );
};

export const UndoModel = types
  .model("UndoStore", {})
  .volatile(() => ({
    undoStack: observable.array<EditorSnapshot>([], { deep: false }),
    redoStack: observable.array<EditorSnapshot>([], { deep: false }),
    isGestureActive: false,
    gestureStartSnapshot: null as EditorSnapshot | null,
    lastSnapshotTime: 0,
    isUndoRedoInProgress: false,
    pendingInitialSnapshot: null as EditorSnapshot | null,
    pendingDebounceTimer: null as ReturnType<typeof setTimeout> | null,
  }))
  .views((self) => ({
    get canUndo() {
      return self.undoStack.length > 0;
    },
    get canRedo() {
      return self.redoStack.length > 0;
    },
  }))
  .actions((self) => ({
    createSnapshot(): EditorSnapshot {
      const root = getRoot<RootInstance>(self) as RootInstance & {
        playerStore?: {
          jsonFile?: unknown;
          jsonFileName?: string | null;
        };
      };

      return {
        modsSnapshot: getSnapshot(root.modsStore.mods) as ModSnapshot,
        jsonFile: cloneJsonSnapshot(root.playerStore?.jsonFile),
        jsonFileName: root.playerStore?.jsonFileName ?? null,
      };
    },

    applyEditorSnapshot(snapshot: EditorSnapshot) {
      const root = getRoot<RootInstance>(self) as RootInstance & {
        playerStore?: {
          applyJsonDraftSnapshot?: (
            jsonFile: unknown,
            jsonFileName?: string | null
          ) => void;
        };
      };

      root.modsStore.applyModsSnapshot(snapshot.modsSnapshot);
      root.playerStore?.applyJsonDraftSnapshot?.(
        snapshot.jsonFile,
        snapshot.jsonFileName ?? undefined
      );
    },

    pushSnapshot(
      snapshot?: EditorSnapshot | ModSnapshot,
      options?: { debounce?: boolean }
    ) {
      if (self.isUndoRedoInProgress) return;

      const normalizedSnapshot = Array.isArray(snapshot)
        ? {
            ...this.createSnapshot(),
            modsSnapshot: snapshot,
          }
        : snapshot ?? this.createSnapshot();

      if (options?.debounce) {
        const now = Date.now();
        if (self.pendingInitialSnapshot === null) {
          self.pendingInitialSnapshot = cloneSnapshot(normalizedSnapshot);
        }
        if (self.pendingDebounceTimer !== null) {
          clearTimeout(self.pendingDebounceTimer);
        }
        self.pendingDebounceTimer = setTimeout(() => {
          this.flushPendingSnapshot();
        }, DEBOUNCE_MS);

        if (now - self.lastSnapshotTime < DEBOUNCE_MS) {
          return;
        }
        self.lastSnapshotTime = now;
        return;
      }

      this.flushPendingSnapshot();
      self.undoStack.push(cloneSnapshot(normalizedSnapshot));

      if (self.undoStack.length > MAX_HISTORY_SIZE) {
        self.undoStack.shift();
      }

      self.redoStack.clear();
    },

    flushPendingSnapshot() {
      if (self.pendingDebounceTimer !== null) {
        clearTimeout(self.pendingDebounceTimer);
        self.pendingDebounceTimer = null;
      }
      if (self.pendingInitialSnapshot !== null) {
        self.undoStack.push(self.pendingInitialSnapshot);
        if (self.undoStack.length > MAX_HISTORY_SIZE) {
          self.undoStack.shift();
        }
        self.redoStack.clear();
        self.pendingInitialSnapshot = null;
        self.lastSnapshotTime = 0;
      }
    },

    undo() {
      this.flushPendingSnapshot();
      if (self.undoStack.length === 0) return;

      const currentSnapshot = this.createSnapshot();
      const previousSnapshot = self.undoStack.pop();
      if (!previousSnapshot) return;

      self.redoStack.push(currentSnapshot);

      self.isUndoRedoInProgress = true;
      try {
        this.applyEditorSnapshot(previousSnapshot);
      } catch (error) {
        console.error("Undo failed:", error);
        self.redoStack.pop();
        self.undoStack.push(previousSnapshot);
      } finally {
        self.isUndoRedoInProgress = false;
      }
    },

    redo() {
      this.flushPendingSnapshot();
      if (self.redoStack.length === 0) return;

      const currentSnapshot = this.createSnapshot();
      const nextSnapshot = self.redoStack.pop();
      if (!nextSnapshot) return;

      self.undoStack.push(currentSnapshot);

      self.isUndoRedoInProgress = true;
      try {
        this.applyEditorSnapshot(nextSnapshot);
      } catch (error) {
        console.error("Redo failed:", error);
        self.undoStack.pop();
        self.redoStack.push(nextSnapshot);
      } finally {
        self.isUndoRedoInProgress = false;
      }
    },

    onGestureStart() {
      if (self.isGestureActive) return;

      self.isGestureActive = true;
      self.gestureStartSnapshot = this.createSnapshot();
    },

    onGestureEnd() {
      if (!self.isGestureActive) return;

      const currentSnapshot = this.createSnapshot();
      const startSnapshot = self.gestureStartSnapshot;

      if (startSnapshot && !snapshotsEqual(startSnapshot, currentSnapshot)) {
        this.pushSnapshot(startSnapshot);
      }

      self.isGestureActive = false;
      self.gestureStartSnapshot = null;
    },

    clearHistory() {
      self.undoStack.clear();
      self.redoStack.clear();
      self.isGestureActive = false;
      self.gestureStartSnapshot = null;
      self.lastSnapshotTime = 0;
      if (self.pendingDebounceTimer !== null) {
        clearTimeout(self.pendingDebounceTimer);
        self.pendingDebounceTimer = null;
      }
      self.pendingInitialSnapshot = null;
    },

    beforeDestroy() {
      if (self.pendingDebounceTimer !== null) {
        clearTimeout(self.pendingDebounceTimer);
      }
    },
  }));

export type IUndoModel = Instance<typeof UndoModel>;
